import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { CourseGroupService } from "../../services/CourseGroupService";
import { ProgramService } from "../../services/ProgramService";
import { sort } from "../../utils/utils";
import { MULTI, Selector } from "./Selector";

export const CourseGroupSelect = ({
  programIds,
  selectHandler,
  clear,
  size,
  interaction,
  width,
  handleSetCourses,
  handleCourseGroupsByProgram,
}) => {
  const { t } = useTranslation();
  const [courseGroups, setCourseGroups] = useState([]);
  const [cachedPrograms, setCachedPrograms] = useState([]);

  useEffect(() => {
    if (programIds.length) {
      const programPromises = [];
      let cgList = [];
      programIds.forEach((pId) => {
        // If program has already been fetched, add programs course groups to list.
        // Otherwise, add program to list of programs to fetch.
        const program = cachedPrograms.find((p) => p.id === pId);
        if (program) {
          cgList = cgList.concat(program.course_groups);
        } else {
          programPromises.push(ProgramService.getCourseGroupsByProgram(pId));
        }
      });
      getCourseGroups(programPromises, cgList);
    }
  }, [programIds]);

  const getCourseGroups = (programPromises, cgList) => {
    Promise.all(programPromises).then((programs) => {
      const cgPromises = [];
      programs.forEach((p) => {
        p.course_groups.forEach((cg, i) => {
          // If course group has already been fetched, set the current course group to the fetched course group.
          // Otherwise, fetch courses for course group.
          if (!cgList.find((group) => group.id === cg.id)) {
            cgPromises.push(CourseGroupService.getCoursesByGroupById(cg.id));
          } else {
            p.course_groups[i] = cgList.find((group) => group.id === cg.id);
          }
        });
      });
      getCoursesFromCourseGroups(cgPromises, cgList, programs);
    });
  };

  const getCoursesFromCourseGroups = (cgPromises, cgList, programs) => {
    Promise.all(cgPromises).then((cgs) => {
      cgs.forEach((cg) => {
        cg.label = cg.name;
        cgList.push(cg);
        // Builds programs obj with course groups with their courses
        programs.forEach((p) => {
          p.course_groups.forEach((courseGroup, i) => {
            if (courseGroup.id === cg.id) {
              p.course_groups[i] = cg;
            }
          });
        });
      });
      // Adds programs to fetchedPrograms state.
      setCachedPrograms((prevFetched) => [...prevFetched, programs].flat());

      // Removes duplicate course groups from list.
      let uniqueList = cgList.filter(
        (item, index, self) => index === self.findIndex((t) => t.id === item.id)
      );
      uniqueList = sort(uniqueList, (cg) => cg.label);

      setCourseGroups(uniqueList);
      if (handleSetCourses) {
        handleSetCourses(uniqueList);
        handleCourseGroupsByProgram(uniqueList);
      }
    });
  };

  const selectWrapper = (courseGroupIds) => {
    selectHandler(courseGroups.filter((cg) => courseGroupIds.includes(cg.id)));
  };

  return (
    <Selector
      type={MULTI}
      id="course-group-select"
      label={t("programs.courseGroup")}
      options={courseGroups}
      selectHandler={selectWrapper}
      assistiveText={t("programs.selectCourseGroups")}
      clear={clear}
      interaction={interaction}
      size={size}
      width={width}
    />
  );
};
